import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CommunicationChannelEnum, IPerson } from '@smarttypes/core';
import { IGuest, IGuestCommunication, IRoom, ISelfOnboard } from '@smarttypes/hotel';
import { catchError, Observable, of, tap, throwError } from 'rxjs';

import { VisitorsAreaService } from './visitors-area.service';

@Injectable({
  providedIn: 'root',
})
export class GuestService {
  private _guest!: IGuest;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private visitorsArea: VisitorsAreaService,
  ) {}

  getGuest(companyId: string, guestId?: string, force?: boolean): Observable<IGuest | null> {
    if (guestId && !guestId.match(/^[0-9a-fA-F]{24}$/)) {
      guestId = undefined;
    }

    if (!guestId && localStorage.getItem('guestId')) {
      guestId = localStorage.getItem('guestId') as string;
    }

    if ((this._guest || !guestId) && !force) {
      return of(this._guest);
    } else {
      return this.httpClient.get<IGuest>(`/hotel/guests/public-info/${companyId}/${guestId}`).pipe(
        catchError(err => {
          localStorage.removeItem('guestId');
          return throwError(() => err);
        }),
        tap(guest => {
          this._guest = guest;

          const langs = ((this._guest?.reservation?.room as IRoom)?.config as any)?.languages ?? [];
          if (langs.length > 0) {
            this.visitorsArea.setLanguages(langs);
          }

          if (guest._id) {
            localStorage.setItem('guestId', guest._id as string);
          }

          if (!this._guest?.reservation?.actions?.guestAreaVisitedAt) {
            this.visitorsArea.visit(guest._id as string);
          }
        }),
      );
    }
  }

  selfOnboard(rq: ISelfOnboard): Observable<IGuest> {
    return (
      this.httpClient.request('post', `/hotel/guests/self-onboard`, {
        body: rq,
      }) as Observable<IGuest>
    ).pipe(
      tap(guest => {
        localStorage.setItem('guestId', guest._id as string);
        this.visitorsArea.visit(guest._id as string);
      }),
    );
  }

  getGuestId(): string {
    return localStorage.getItem('guestId') ?? '';
  }

  removeGuestId(): void {
    localStorage.removeItem('guestId');
  }

  updateGuestWhatsApp(companyId: string, guestId: string, whatsApp: string) {
    return this.httpClient.request('post', `/visitors-area-v2/public/update-contact`, {
      body: {
        companyId,
        id: guestId,
        channel: CommunicationChannelEnum.WhatsApp,
        value: whatsApp,
      },
    });
  }

  openChannel(
    channel: CommunicationChannelEnum,
    link: string,
    receptionNumber?: string,
    smartAi?: boolean,
  ): void | Promise<boolean> {
    switch (channel) {
      case CommunicationChannelEnum.FacebookMessenger:
        window.open(`${link}?ref=${this._guest._id as string}`, '_self');
        break;
      case CommunicationChannelEnum.WhatsApp:
        window.open(this.#buildWhatsAppUrl(link), '_self');
        break;
      case CommunicationChannelEnum.SmartChat:
        this.router.navigate(['/webchat'], {
          queryParams: {
            smartAi,
          },
        });
        break;
      case CommunicationChannelEnum.Phone:
        if (receptionNumber) {
          window.open(`tel:${receptionNumber}`, '_self');
        }
        break;
      default:
        this.router.navigate(['/']);
    }
  }

  #buildWhatsAppUrl(link: string): string {
    const isWebUrl =
      link.includes('whatsapp://') || link.startsWith('https://') || link.startsWith('http://');

    if (isWebUrl) {
      return link;
    }

    return `whatsapp://send?phone=+${link}`;
  }

  addRoommate(
    roommates: {
      parentId?: string;
      person?: IPerson;
      communication?: IGuestCommunication;
      companyId?: string;
    }[],
  ): Observable<{ success: boolean }> {
    return this.httpClient.post<{ success: boolean }>(`/hotel/guests/room-mate`, {
      roomMates: roommates,
    });
  }
}
